<script lang="ts">
  import { tick } from 'svelte';
  import { isCoarsePointer, viewport } from '../lib/stores/viewport';
  import { router } from '@inertiajs/svelte';
  import type { CalendarEventType } from '../lib/types';
  import {
    eventModalOpen,
    eventModalDate,
    eventModalEvent,
    eventModalStartTime,
    closeEventModal,
  } from '../lib/stores/ui';
  import { undoableSoftDelete } from '../lib/undoManager';
  import { jsonHeaders } from '../lib/apiHelpers';
  import { toast } from 'svelte-sonner';
  import { useModalAnimation } from '../lib/useModalAnimation.svelte';
  import ResponsiveOverlay from './ResponsiveOverlay.svelte';

  const modal = useModalAnimation(() => $eventModalOpen, closeEventModal);

  interface Props {
    monthKey: string;
  }

  let { monthKey }: Props = $props();

  let title = $state('');
  let date = $state('');
  let endDate = $state('');
  let allDay = $state(true);
  let startTime = $state('09:00');
  let endTime = $state('10:00');
  let eventType = $state<CalendarEventType>('event');
  let notes = $state('');
  let isSubmitting = $state(false);
  let titleInput: HTMLInputElement | undefined = $state();
  let cardEl: HTMLDivElement | undefined = $state();

  const eventTypes: { value: CalendarEventType; label: string; icon: string }[] = [
    { value: 'appointment', label: 'Appointment', icon: '📅' },
    { value: 'travel', label: 'Travel', icon: '✈️' },
    { value: 'event', label: 'Event', icon: '🎉' },
    { value: 'deadline', label: 'Deadline', icon: '⏰' },
  ];

  let isEditing = $derived(!!$eventModalEvent);
  let modalTitle = $derived(isEditing ? 'Edit Event' : 'New Calendar Event');

  // Validation
  // End time is invalid only if it's set AND before/equal to start time (same day)
  let endTimeInvalid = $derived(!allDay && endTime && endTime <= startTime && !endDate);
  let endDateInvalid = $derived(endDate && endDate < date);

  // Initialize form when modal opens
  // eslint-disable-next-line momentium/no-unguarded-effect -- form init lifecycle: must reset all form fields when modal opens/closes and populate from store state
  $effect(() => {
    if ($eventModalOpen) {
      const event = $eventModalEvent;

      if (event) {
        // Editing existing event
        title = event.title;
        date = event.date;
        endDate = event.endDate || '';
        allDay = !event.startTime;
        startTime = event.startTime || '09:00';
        // Preserve empty end time for zero-duration events
        endTime = event.endTime || '';
        eventType = event.type;
        notes = event.notes || '';
      } else {
        // Creating new event
        title = '';
        endDate = '';
        allDay = true;
        startTime = '09:00';
        endTime = '10:00';
        eventType = 'event';
        notes = '';

        if ($eventModalStartTime) {
          allDay = false;
          startTime = $eventModalStartTime;
          endTime = '';
        }
        if ($eventModalDate) {
          date = $eventModalDate;
        } else {
          // Default to first of current month
          date = `${monthKey}-01`;
        }
      }
      isSubmitting = false;

      // Focus title input after DOM updates. On touch, focus the dialog instead so
      // screen readers start inside it without the keyboard covering the form.
      tick().then(() => {
        if (isCoarsePointer()) {
          cardEl?.focus();
        } else if (titleInput) {
          titleInput.focus();
          titleInput.setSelectionRange(titleInput.value.length, titleInput.value.length);
        }
      });
    }
  });

  function handleSubmit(e: Event) {
    e.preventDefault();
    if (!title.trim() || !date || isSubmitting) return;
    if (endDateInvalid || endTimeInvalid) return;

    isSubmitting = true;
    const event = $eventModalEvent;

    const payload = {
      calendar_event: {
        title: title.trim(),
        date: date,
        end_date: endDate || null,
        start_time: allDay ? null : startTime,
        end_time: allDay ? null : endTime || null, // null if empty (zero-duration event)
        event_type: eventType,
        notes: notes.trim() || null,
      },
    };

    if (isEditing && event) {
      // Update existing event
      router.patch(`/calendar_events/${event.id}`, payload, {
        preserveScroll: true,
        onSuccess: () => closeEventModal(),
        onError: (errors) => {
          isSubmitting = false;
          toast.error(firstInertiaError(errors) ?? 'Could not save event');
        },
      });
    } else {
      // Create new event
      router.post('/calendar_events', payload, {
        preserveScroll: true,
        onSuccess: () => closeEventModal(),
        onError: (errors) => {
          isSubmitting = false;
          toast.error(firstInertiaError(errors) ?? 'Could not create event');
        },
      });
    }
  }

  function firstInertiaError(errors: Record<string, unknown> | undefined): string | null {
    if (!errors) return null;
    const first = Object.values(errors).flat().find(Boolean);
    return typeof first === 'string' && first.trim() ? first : null;
  }

  function handleDelete() {
    const event = $eventModalEvent;
    if (!event) return;

    const eventId = event.id;
    const eventPreview = event.title.length > 40 ? event.title.slice(0, 40) + '…' : event.title;

    // Close modal immediately
    closeEventModal();

    undoableSoftDelete({
      message: `Deleted "${eventPreview}"`,
      onOptimisticRemove: () => {
        // No immediate UI change needed — Inertia reload handles it
      },
      onOptimisticRestore: () => {
        // No immediate UI change — the restore is handled in onRestore
        // by reloading from the server.
      },
      onDelete: async () => {
        const response = await fetch(`/calendar_events/${eventId}`, {
          method: 'DELETE',
          headers: jsonHeaders(),
        });
        if (!response.ok) throw new Error('Server error');
        router.reload();
      },
      onRestore: async () => {
        const response = await fetch(`/calendar_events/${eventId}/undiscard`, {
          method: 'POST',
          headers: jsonHeaders(),
        });
        if (!response.ok) throw new Error('Server error');
        router.reload();
      },
    });
  }

  /** Cancel: animate the desktop modal out; the mobile sheet slides itself out. */
  function dismiss() {
    if ($viewport.isMobile) closeEventModal();
    else modal.animateClose();
  }

  function handleKeydown(e: KeyboardEvent) {
    // On mobile the bottom sheet handles Esc itself.
    if (e.key === 'Escape' && !$viewport.isMobile) {
      modal.animateClose();
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<!-- Desktop: centered modal. Mobile: full-height bottom sheet, actions pinned at the bottom. -->
<ResponsiveOverlay
  open={$eventModalOpen}
  onClose={closeEventModal}
  detents={['full']}
  title={modalTitle}
>
  {#snippet desktop(content)}
    {#if $eventModalOpen}
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div
        class="modal-overlay"
        class:closing={modal.closing}
        bind:this={modal.overlayEl}
        onclick={modal.animateClose}
        role="presentation"
      >
        <div
          class="modal-card"
          bind:this={cardEl}
          onclick={(e) => e.stopPropagation()}
          role="dialog"
          tabindex="-1"
        >
          <!-- Header -->
          <div class="flex items-center justify-between border-b border-gray-200 px-5 py-4">
            <h2 class="text-lg font-semibold text-gray-900">{modalTitle}</h2>
            <button
              type="button"
              class="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              onclick={modal.animateClose}
              aria-label="Close modal"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          {@render content()}
        </div>
      </div>
    {/if}
  {/snippet}

  <!-- Form -->
  <form
    onsubmit={handleSubmit}
    class="event-form p-5"
    class:sheet={$viewport.isMobile}
    data-sheet-fill
  >
    <div class="form-fields">
      <!-- Title -->
      <div class="mb-4">
        <label for="event-title" class="mb-1 block text-sm font-medium text-gray-700">
          Title
        </label>
        <input
          autocomplete="off"
          id="event-title"
          type="text"
          bind:this={titleInput}
          bind:value={title}
          data-sheet-autofocus
          placeholder="Event title..."
          class="w-full rounded-lg border border-gray-300 px-3 py-2 transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>

      <!-- Dates -->
      <div class="mb-4">
        <div class="date-row">
          <div class="date-field">
            <label for="event-date" class="mb-1 block text-sm font-medium text-gray-700">
              Date
            </label>
            <input
              autocomplete="off"
              id="event-date"
              type="date"
              bind:value={date}
              class="w-full rounded-lg border border-gray-300 px-3 py-2 transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div class="date-field">
            <label for="event-end-date" class="mb-1 block text-sm font-medium text-gray-700">
              End Date <span class="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              autocomplete="off"
              id="event-end-date"
              type="date"
              bind:value={endDate}
              min={date}
              class="w-full rounded-lg border border-gray-300 px-3 py-2 transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        {#if endDateInvalid}
          <p class="mt-1 text-xs text-red-500">End date must be on or after start date</p>
        {/if}
      </div>

      <!-- All Day Toggle -->
      <div class="mb-4">
        <label class="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            bind:checked={allDay}
            class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span class="text-sm font-medium text-gray-700">All day</span>
        </label>
      </div>

      <!-- Time inputs (only shown when not all-day) -->
      {#if !allDay}
        <div class="time-section mb-4">
          <div class="time-row">
            <div class="time-field">
              <label for="event-start-time" class="mb-1 block text-sm font-medium text-gray-700">
                Start Time
              </label>
              <input
                autocomplete="off"
                id="event-start-time"
                type="time"
                bind:value={startTime}
                class="w-full rounded-lg border border-gray-300 px-3 py-2 transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div class="time-field">
              <label for="event-end-time" class="mb-1 block text-sm font-medium text-gray-700">
                End Time <span class="font-normal text-gray-400">(optional)</span>
              </label>
              <input
                autocomplete="off"
                id="event-end-time"
                type="time"
                bind:value={endTime}
                placeholder="--:--"
                class="w-full rounded-lg border border-gray-300 px-3 py-2 transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          {#if endTimeInvalid}
            <p class="mt-1 text-xs text-red-500">End time must be after start time</p>
          {/if}
        </div>
      {/if}

      <!-- Event Type -->
      <div class="mb-4" role="group" aria-labelledby="event-type-label">
        <span id="event-type-label" class="mb-2 block text-sm font-medium text-gray-700">
          Type
        </span>
        <div class="grid grid-cols-2 gap-2">
          {#each eventTypes as type}
            <button
              type="button"
              class="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors
                   {eventType === type.value
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-200 text-gray-600 hover:border-gray-300'}"
              onclick={() => (eventType = type.value)}
            >
              <span>{type.icon}</span>
              <span>{type.label}</span>
            </button>
          {/each}
        </div>
      </div>

      <!-- Notes -->
      <div class="mb-6">
        <label for="event-notes" class="mb-1 block text-sm font-medium text-gray-700">
          Notes <span class="font-normal text-gray-400">(optional)</span>
        </label>
        <textarea
          id="event-notes"
          bind:value={notes}
          placeholder="Additional details..."
          rows="2"
          class="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
        ></textarea>
      </div>
    </div>

    <!-- Actions -->
    <div class="form-actions flex justify-between">
      <div>
        {#if isEditing}
          <button
            type="button"
            class="rounded-lg px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
            onclick={handleDelete}
            disabled={isSubmitting}
          >
            Delete
          </button>
        {/if}
      </div>
      <div class="flex gap-3">
        <button
          type="button"
          class="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
          onclick={dismiss}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          class="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={isSubmitting || !title.trim() || endDateInvalid || endTimeInvalid}
        >
          {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Event'}
        </button>
      </div>
    </div>
  </form>
</ResponsiveOverlay>

<style>
  .modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    animation: overlay-in 150ms ease-out both;
  }

  .modal-overlay.closing {
    animation: overlay-out 100ms ease-in both;
  }

  .modal-overlay.closing .modal-card {
    animation: card-out 100ms ease-in both;
  }

  .modal-card {
    outline: none;
    background: white;
    border-radius: 0.75rem;
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 8px 10px -6px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 28rem;
    margin: 0 1rem;
    overflow: hidden;
    animation: card-in 150ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  @keyframes overlay-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes overlay-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes card-in {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes card-out {
    from {
      transform: scale(1);
      opacity: 1;
    }
    to {
      transform: scale(0.95);
      opacity: 0;
    }
  }

  /* minmax(0, …) and min-width: 0 let date inputs shrink below their intrinsic
     width, so End Date can't overflow the dialog on narrow screens. */
  .date-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .date-field {
    min-width: 0;
  }

  .date-field input {
    min-width: 0;
    max-width: 100%;
  }

  .time-section {
    padding: 0.75rem;
    background: #f9fafb;
    border-radius: 0.5rem;
    border: 1px solid #e5e7eb;
  }

  .time-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }

  /* Mobile bottom sheet: fields scroll, actions stay pinned above the safe area. */
  .event-form.sheet {
    display: flex;
    flex-direction: column;
    padding: 0;
  }

  .event-form.sheet .form-fields {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 1rem;
  }

  .event-form.sheet .form-actions {
    flex-shrink: 0;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-top: 1px solid #e5e7eb;
    background: white;
  }

  .event-form.sheet button,
  .event-form.sheet input:not([type='checkbox']) {
    min-height: var(--mobile-tap);
  }

  .event-form.sheet label:has(> input[type='checkbox']) {
    min-height: var(--mobile-tap);
  }
</style>
